import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { grades } from '../services/api';
import { nomUsuari } from '../utils/usuari';

function MyGradesPage() {
  const [gradeList, setGradeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadGrades();
  }, []);

  const loadGrades = async () => {
    try {
      setLoading(true);
      const { data } = await grades.getMyGrades();
      setGradeList(data);
    } catch (err) {
      setError('Error carregant les qualificacions');
      console.error('Error loading grades:', err);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (grade >= 90) return 'text-success-text bg-success-subtle';
    if (grade >= 70) return 'text-accent-hover bg-accent-subtle';
    if (grade >= 50) return 'text-warning-text bg-warning-subtle';
    return 'text-error-text bg-error-subtle';
  };

  // Els exercicis acabats per temps esgotat o abandonament ara també arriben aquí,
  // perquè el servidor els inclou i es poden qualificar. Sense un cas propi queien
  // al default i l'alumne els veia sense cap etiqueta, sense saber per què hi eren.
  const getStatusBadge = (status) => {
    switch (status) {
      case 'graded':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-subtle text-success-text">Qualificat</span>;
      case 'submitted':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-subtle text-warning-text">Pendent de revisió</span>;
      case 'time_expired':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-subtle text-warning-text">Temps esgotat · pendent de revisió</span>;
      case 'abandoned':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-error-subtle text-error-text">Abandonat · pendent de revisió</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Les meves qualificacions</h1>
          <p className="text-content-secondary">Consulta les qualificacions i feedback dels teus exercicis</p>
        </div>
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {gradeList.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="w-16 h-16 text-content-disabled mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-content mb-2">Cap qualificació encara</h3>
          <p className="text-content-muted">Quan completis exercicis i siguin qualificats, apareixeran aquí.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {gradeList.map((item) => (
            <div key={item.assessmentId} className="card hover:border-border-strong transition-colors">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === item.assessmentId ? null : item.assessmentId)}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-semibold text-content">{item.case.title}</h3>
                    {item.case.isTeamBased && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-subtle text-accent">
                        Equip
                      </span>
                    )}
                    {getStatusBadge(item.status)}
                  </div>
                  <p className="text-sm text-content-muted mt-1">
                    Projecte: {item.project.title}
                    {item.team && ` · Equip: ${item.team.name}`}
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  {item.grade ? (
                    <div className={`px-4 py-2 rounded-lg font-bold text-lg ${getGradeColor(item.grade.grade)}`}>
                      {item.grade.grade}/100
                    </div>
                  ) : (
                    <div className="px-4 py-2 rounded-lg bg-surface-muted text-content-muted font-medium">
                      Pendent
                    </div>
                  )}
                  <svg
                    className={`w-5 h-5 text-content-disabled transition-transform ${expandedId === item.assessmentId ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedId === item.assessmentId && (
                <div className="mt-4 pt-4 border-t border-border-subtle">
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-content-muted">Enviat:</span>
                      <span className="ml-2 text-content">
                        {item.submittedAt ? new Date(item.submittedAt).toLocaleString('ca-ES') : '-'}
                      </span>
                    </div>
                    {item.grade && (
                      <>
                        <div>
                          <span className="text-content-muted">Qualificat:</span>
                          <span className="ml-2 text-content">
                            {item.grade.gradedAt ? new Date(item.grade.gradedAt).toLocaleString('ca-ES') : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-content-muted">Qualificat per:</span>
                          <span className="ml-2 text-content">
                            {item.grade.gradedBy ? nomUsuari(item.grade.gradedBy, '-') : '-'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {item.grade?.feedback && (
                    <div className="bg-surface-subtle rounded-lg p-4">
                      <h4 className="text-sm font-medium text-content-secondary mb-2">Feedback de l'instructor</h4>
                      <p className="text-content-secondary whitespace-pre-wrap">{item.grade.feedback}</p>
                    </div>
                  )}

                  {!item.grade?.feedback && item.grade && (
                    <p className="text-sm text-content-muted italic">L'instructor no ha afegit comentaris.</p>
                  )}

                  <div className="mt-4 flex justify-end">
                    <Link
                      to={`/assessments/${item.assessmentId}`}
                      className="text-accent hover:text-accent-hover text-sm font-medium"
                    >
                      Veure l'avaluació completa
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {gradeList.length > 0 && (
        <div className="card bg-surface-subtle">
          <h3 className="font-semibold text-content mb-4">Resum</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-content">{gradeList.length}</p>
              <p className="text-sm text-content-muted">Total exercicis</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-success">
                {gradeList.filter(g => g.grade).length}
              </p>
              <p className="text-sm text-content-muted">Qualificats</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-accent">
                {gradeList.filter(g => g.grade).length > 0
                  ? Math.round(gradeList.filter(g => g.grade).reduce((acc, g) => acc + g.grade.grade, 0) / gradeList.filter(g => g.grade).length)
                  : '-'}
              </p>
              <p className="text-sm text-content-muted">Mitjana</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyGradesPage;
